import AppAlert from '@nxsys/components/AppAlert';
import AppCard from '@nxsys/components/AppCard';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useGetReversalPayrolls } from '@nxsys/hooks/api/timesheets';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { ReversalPayrollSubcontractor } from '@nxsys/types/models/timesheet';
import { IRELAND } from '@nxsys/store/constants/keys';
import { showCurrencyByCountry } from '@nxsys/utils';
import { showToast } from '@nxsys/utils/toast';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Col, Row, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { concat, forEach, includes, isEmpty, sumBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { PerformReversalPayrollsParams } from '@nxsys/types/api';

const { Text } = Typography;

interface TimesheetReverseModalProps {
  isOpen: boolean;
  toggle: () => void;
  payrollIds: string[];
  onPerform?: (
    params: Omit<PerformReversalPayrollsParams, 'isTimesheet'>,
  ) => void;
}

interface SubcontractorReverse extends ReversalPayrollSubcontractor {
  totalGross: number;
  totalNet: number;
}

const TimesheetReverseModal: React.FC<TimesheetReverseModalProps> = ({
  isOpen,
  toggle,
  payrollIds,
  onPerform,
}) => {
  const { messages } = useTypedIntl();

  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const {
    run: getReversalPayrolls,
    value: reversalPayrolls,
    loading: isGetReversalPayrollsPending,
  } = useGetReversalPayrolls();
  const subcontractorList: SubcontractorReverse[] = useMemo(
    () =>
      reversalPayrolls?.data
        ?.map((p) => p.subcontractor)
        ?.map((subcontractor) => ({
          ...subcontractor,
          totalGross: sumBy(subcontractor.payroll, (p) => Number(p.gross)) ?? 0,
          totalNet: sumBy(subcontractor.payroll, (p) => Number(p.net)) ?? 0,
        })) ?? [],
    [reversalPayrolls],
  );

  const isIreland = payrollCountry === IRELAND;
  const [isInvalidPayroll, setIsInvalidPayroll] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsInvalidPayroll(false);
      getReversalPayrolls({ ids: payrollIds });
    }
  }, [payrollIds, isOpen]);

  // Ireland: warn if payrolls processed under previous employment or contributions already submitted
  useEffect(() => {
    if (isIreland && reversalPayrolls?.isInvalidPayroll) {
      setIsInvalidPayroll(true);
      showToast({
        type: 'warning',
        message:
          'The payrolls were processed under a previous employment, or My Future Fund contributions have already been submitted, so they cannot be reversed.',
      });
    }
  }, [reversalPayrolls, isIreland]);

  const [selectedRowKey, setSelectedRowKey] = useState<React.Key[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    setSelectedRowKey(
      subcontractorList?.map((item) => item.id ?? '').filter((id) => !!id) ??
        [],
    );
  }, [subcontractorList]);

  const columns: ColumnsType<any> = [
    {
      title: messages['timesheet.employeeId'],
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: isBureau ? messages['table.employer'] : messages['table.paymentCompany'],
      dataIndex: 'companyPayment',
      key: 'paymentCompany',
      render: (value) => value?.companyName ?? '',
    },
    {
      title: messages['timesheet.grossPayment'],
      dataIndex: 'totalGross',
      key: 'totalGross',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: messages['table.netPay'],
      dataIndex: 'totalNet',
      key: 'totalNet',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
  ];

  const expandedRowRender = (record: any) => {
    return (
      <Row gutter={[16, 16]}>
        {record.payroll?.map((payroll: any) => (
          <Col key={payroll.id} sm={24} md={12}>
            <div className="rounded-md border border-neutral-200 bg-neutral-50">
              <div className="p-[12px] text-[14px] text-neutral-700 font-[500] border-b border-neutral-100">
                {`${messages['common.payroll']} ${payroll.id}`}
              </div>
              <div className="py-[16px] px-[12px]">
                <div className="space-y-[16px]">
                  <div className="grid grid-cols-2 gap-[8px]">
                    <div className="text-[14px] text-neutral-400 font-[400]">
                      {messages['common.taxPeriod']}
                    </div>
                    <div className="text-[14px] text-neutral-700 font-[500]">
                      {payroll.taxPeriod ?? ''}
                    </div>
                  </div>
                  {!isBureau && (
                    <div className="grid grid-cols-2 gap-[8px]">
                      <div className="text-[14px] text-neutral-400 font-[400]">
                        {messages['common.agencyName']}
                      </div>
                      <div className="text-[14px] text-neutral-700 font-[500]">
                        {payroll.agencyRef?.name ?? ''}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-[8px]">
                    <div className="text-[14px] text-neutral-400 font-[400]">
                      {messages['timesheet.grossPay']}
                    </div>
                    <div className="text-[14px] text-neutral-700 font-[500]">
                      {formatMoney(
                        payroll.gross,
                        showCurrencyByCountry(payrollCountry),
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-[8px]">
                    <div className="text-[14px] text-neutral-400 font-[400]">
                      {messages['timesheet.netPay']}
                    </div>
                    <div className="text-[14px] text-neutral-700 font-[500]">
                      {formatMoney(
                        payroll.net,
                        showCurrencyByCountry(payrollCountry),
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-[8px]">
                    <div className="text-[14px] text-neutral-400 font-[400]">
                      {messages['common.payslipGenerated']}
                    </div>
                    <div className="text-[14px] text-neutral-700 font-[500]">
                      {payroll?.selfBillRef?.id ? (
                        <Icons.IconCheckCircle />
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    );
  };

  const rowSelection = {
    selectedRowKeys: selectedRowKey,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKey(selectedKeys);
    },
  };

  const handlePerform = () => {
    if (onPerform) {
      const selectedSubcontractors = subcontractorList?.filter((item) => {
        return includes(selectedRowKey, item.id);
      });
      let prs: any[] = [];
      let dprs: any[] = [];
      forEach(selectedSubcontractors, (item) => {
        const { payroll, draftPayrolls } = item;
        if (!isEmpty(payroll)) {
          prs = concat(prs, payroll);
        }
        if (!isEmpty(draftPayrolls)) {
          dprs = concat(dprs, draftPayrolls);
        }
      });
      onPerform({
        payrollIds: prs.map((pr) => pr.id),
        draftPayrollIds: dprs.map((drp) => drp.id),
      });
    }
    toggle();
  };

  const handleCancel = () => {
    toggle();
  };

  return (
    <AppModalContainer
      title={messages['timesheet.reversePayrolls']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: handleCancel,
        },
        {
          name: messages['timesheet.perform'],
          buttonType: 'primary',
          onClick: handlePerform,
          disabled: selectedRowKey.length === 0 || isInvalidPayroll,
        },
      ]}
      width={'60vw'}
      className="max-sm:!w-[90vw]"
    >
      <AppAlert
        className="mb-[24px]"
        variant="warning"
        type="warning"
        showIcon
        message={
          <div>
            <Text strong>
              {selectedRowKey.length === 1
                ? messages['timesheet.reverse.warning.titleSingular']
                : messages['timesheet.reverse.warning.title']}
            </Text>
            <ul className="mt-2 mb-0 pl-[30px] list-disc">
              <li>{messages['timesheet.reverse.warning.item1']}</li>
              <li>{messages['timesheet.reverse.warning.item2']}</li>
              <li>{messages['timesheet.reverse.warning.item3']}</li>
              <li>
                {isIreland
                  ? messages['timesheet.reverse.warning.item4ForIreland']
                  : messages['timesheet.reverse.warning.item4']}
              </li>
            </ul>
          </div>
        }
      />

      <AppCard>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={subcontractorList ?? []}
            columns={columns}
            loading={isGetReversalPayrollsPending}
            page={0}
            pageSize={10}
            total={subcontractorList?.length ?? 0}
            rowSelection={rowSelection}
            expandable={{
              expandedRowRender,
              expandedRowKeys,
              onExpand: (expanded: boolean, record: any) => {
                setExpandedRowKeys((prev) =>
                  expanded
                    ? [...prev, record.id]
                    : prev.filter((key) => key !== record.id),
                );
              },
              expandIcon: ({ expanded }: any) =>
                expanded ? (
                  <div
                    className="w-[32px] h-[32px] rounded-[8px] p-[8px] bg-primary-50 cursor-pointer"
                    style={{ transform: 'translateX(-8px)' }}
                  >
                    <Icons.IconAngleUp width={16} height={16} />
                  </div>
                ) : (
                  <div
                    className="w-[32px] h-[32px] rounded-[8px] p-[8px] bg-primary-50 cursor-pointer"
                    style={{ transform: 'translateX(-8px)' }}
                  >
                    <Icons.IconAngleDown width={16} height={16} />
                  </div>
                ),
            }}
            onRow={(record: any) => ({
              onClick: () => {
                setExpandedRowKeys((prev) =>
                  prev.includes(record.id)
                    ? prev.filter((key) => key !== record.id)
                    : [...prev, record.id],
                );
              },
              style: { cursor: 'pointer' },
            })}
            pagination={false}
            scroll={{ x: 800, y: 500 }}
            titleEmpty={messages['timesheet.reverse.empty']}
          />
        </AppsContent>
      </AppCard>
    </AppModalContainer>
  );
};

export default TimesheetReverseModal;
